import { getAddress } from '@ethersproject/address'
import { Contract } from '@ethersproject/contracts'
import { AddressZero } from '@ethersproject/constants'
import { JsonRpcSigner, Web3Provider } from '@ethersproject/providers'
import { BigNumber } from '@ethersproject/bignumber'
import { abi as IUniswapV2Router02ABI } from '@uniswap/v2-periphery/build/IUniswapV2Router02.json'
import { JSBI, Percent, Token, Currency, ETHER } from 'sdk'
import { BASE_FTM_SCAN_URLS } from 'config'
import tokens from 'config/constants/tokens'
import { Token as TokenP } from 'config/constants/types'
import { getAddress as getPAddress } from 'utils/addressHelpers'
import { ROUTER_ADDRESS } from '../config/constants'

// returns the checksummed address if the address is valid, otherwise returns false
export function isAddress(value: any): string | false {
  try {
    return getAddress(value)
  } catch {
    return false
  }
}

export function getFtmScanLink(
  data: string | number,
  type: 'transaction' | 'token' | 'address' | 'block' | 'countdown',
  chainId = 250,
): string {
  switch (type) {
    case 'transaction': {
      return `${BASE_FTM_SCAN_URLS[chainId]}/tx/${data}`
    }
    case 'token': {
      return `${BASE_FTM_SCAN_URLS[chainId]}/token/${data}`
    }
    case 'block': {
      return `${BASE_FTM_SCAN_URLS[chainId]}/block/${data}`
    }
    case 'countdown': {
      return `${BASE_FTM_SCAN_URLS[chainId]}/block/countdown/${data}`
    }
    default: {
      return `${BASE_FTM_SCAN_URLS[chainId]}/address/${data}`
    }
  }
}

// shorten the checksummed version of the input address to have 0x + 4 characters at start and end
export function shortenAddress(address: string, chars = 4): string {
  const parsed = isAddress(address)
  if (!parsed) {
    throw Error(`Invalid 'address' parameter '${address}'.`)
  }
  return `${parsed.substring(0, chars + 2)}...${parsed.substring(42 - chars)}`
}

// add 10%
export function calculateGasMargin(value: BigNumber): BigNumber {
  return value.mul(BigNumber.from(10000).add(BigNumber.from(1000))).div(BigNumber.from(10000))
}

// converts a basis points value to a sdk percent
export function basisPointsToPercent(num: number): Percent {
  return new Percent(JSBI.BigInt(num), JSBI.BigInt(10000))
}

// account is not optional
export function getSigner(library: Web3Provider, account: string): JsonRpcSigner {
  return library.getSigner(account).connectUnchecked()
}

// account is optional
export function getProviderOrSigner(library: Web3Provider, account?: string): Web3Provider | JsonRpcSigner {
  return account ? getSigner(library, account) : library
}

// account is optional
export function getContract(address: string, ABI: any, library: Web3Provider, account?: string): Contract {
  if (!isAddress(address) || address === AddressZero) {
    throw Error(`Invalid 'address' parameter '${address}'.`)
  }

  return new Contract(address, ABI, getProviderOrSigner(library, account) as any)
}

// account is optional
export function getRouterContract(_: number, library: Web3Provider, account?: string): Contract {
  return getContract(ROUTER_ADDRESS, IUniswapV2Router02ABI, library, account)
}

export function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') // $& means the whole matched string
}

export function isTokenOnList(defaultTokens, currency?: Currency): boolean {
  if (currency === ETHER) return true
  return Boolean(currency instanceof Token && defaultTokens[currency.chainId]?.[currency.address])
}

export const getPngImageUrlFromString = (address: string) => {
  return `/images/tokens/${address}.png`
}

export const getPngImageUrlFromToken = (token: TokenP) => {
  const address = getPAddress(token.symbol === 'FTM' ? tokens.wftm.address : token.address)
  return getPngImageUrlFromString(address)
}

const np = (i, n = 2) => parseFloat(i).toLocaleString(undefined, { maximumFractionDigits: n })
/**
 * Format values
 * i.e. Formats 9763410 into 9.76M (3 display decimals)
 */
export const formatValue = (i) => {
  if (i < 10) {
    return np(i, 2)
  }
  if (i < 100) {
    return np(i, 1)
  }
  if (i < 1000) {
    return np(i, 0)
  }
  if (i < 10000) {
    return `${np(i / 1000, 2)}K`
  }
  if (i < 100000) {
    return `${np(i / 1000, 1)}K`
  }
  if (i < 1000000) {
    return `${np(i / 1000, 0)}K`
  }
  if (i < 10000000) {
    return `${np(i / 1000000, 2)}M`
  }
  if (i < 100000000) {
    return `${np(i / 1000000, 1)}M`
  }
  return `${np(i / 1000000, 0)}M`
}

const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]
export const formatDateFromObject = (dateObject: Date) => {
  return `${months[dateObject.getMonth()]} ${dateObject.getDate()}, ${dateObject.getFullYear()}`
}

export const formatDate = (date: string) => {
  return formatDateFromObject(new Date(date))
}
